import { RouterProvider, createBrowserRouter, Outlet } from 'react-router-dom';
import { useEffect, useCallback } from 'react';
import { feedbackDataManager } from '@/lib/managers/FeedbackDataManager';
import MainPage from './pages/MainPage';
import '@arco-design/web-react/dist/css/arco.css';
import { SceneThemeProvider } from '@/lib/contexts/SceneThemeContext';

import logger from '@/utils/logger';

function SceneThemeLayout() {
  return (
    <SceneThemeProvider>
      <Outlet />
    </SceneThemeProvider>
  );
}

// 使用 Data Router 配置，并启用 v7 future flags
// 支持两种布局参数形式：
// 1. 路径参数: /scene/elder/layout4
// 2. 查询参数: /scene/elder?layout4=true（向后兼容）
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <SceneThemeLayout />,
      children: [
        { index: true, element: <MainPage /> },
        { path: 'scene/:sceneId/:layoutType', element: <MainPage /> },
        { path: 'scene/:sceneId', element: <MainPage /> },
        { path: '*', element: <MainPage /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

function App() {
  // 开发环境下输出 Debug 级别提示，避免噪声
  if (process.env.NODE_ENV === 'development') {
    logger.debug('[App] 运行问题可参考 README 内容进行排查');
  }

  // 消费者逻辑：上传存储的所有反馈数据
  const uploadStoredFeedbacks = useCallback(async () => {
    try {
      const result = await feedbackDataManager.uploadStoredData();
      logger.info(`[App] 反馈数据上传完成: 成功 ${result.successful} 条, 失败 ${result.failed} 条`);
    } catch (error) {
      logger.warn('[App] 上传存储的反馈数据失败:', error);
    }
  }, []);

  // 检查并清理刷新参数
  const checkRefreshState = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refreshed = urlParams.get('refreshed');
    const reason = urlParams.get('reason');

    if (refreshed) {
      logger.info('[App] 页面刷新完成，原因:', decodeURIComponent(reason || ''));

      // 立即恢复背景图片配置，避免闪烁（优先按场景命名空间，其次兼容旧键）
      const path = window.location.pathname;
      const match = path.match(/\/scene\/([^/?#]+)/i);
      const sceneId = match ? decodeURIComponent(match[1]) : 'Custom';
      const nsBg = sessionStorage.getItem(`theme:${sceneId}:bg`);
      const nsHero = sessionStorage.getItem(`theme:${sceneId}:hero`);
      const legacyBg = sessionStorage.getItem('themeBackgroundImage');
      const legacyHero = sessionStorage.getItem('themeHeroCSS');
      const cachedBackgroundImage = nsBg || legacyBg;
      const cachedThemeHeroCSS = nsHero || legacyHero;

      if (cachedThemeHeroCSS) {
        // 使用requestAnimationFrame确保在下一帧立即应用
        requestAnimationFrame(() => {
          document.documentElement.style.setProperty('--theme-hero', cachedThemeHeroCSS);
          if (cachedBackgroundImage) {
            logger.info('[App] 已恢复缓存的背景图片:', cachedBackgroundImage);
          }
        });
      }

      // 清理刷新相关的URL参数，保留其他重要参数
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('refreshed');
      currentUrl.searchParams.delete('reason');
      // 保留layout2 / layout3 / layout4等其他重要参数
      window.history.replaceState(null, '', currentUrl.toString());
    }
  }, []);

  // 全局事件处理 - 增强用户体验
  const setupGlobalEventHandlers = useCallback(() => {
    // 禁用全局右键菜单（在生产环境）
    const handleContextMenu = (e: MouseEvent) => {
      // 在开发环境允许右键菜单，生产环境禁用
      if (process.env.NODE_ENV === 'production') {
        e.preventDefault();
      }
    };

    // 禁用文本选择（双击、三击等）- 仅生产环境
    const handleSelectStart = (e: Event) => {
      if (process.env.NODE_ENV !== 'production') return;
      const target = e.target as HTMLElement;

      // 允许输入框和可编辑元素的选择
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true' ||
        target.closest('.arco-input') ||
        target.closest('.arco-textarea')
      ) {
        return;
      }

      e.preventDefault();
    };

    // 禁用拖拽 - 仅生产环境
    const handleDragStart = (e: DragEvent) => {
      if (process.env.NODE_ENV !== 'production') return;
      const target = e.target as HTMLElement;

      // 允许特定元素的拖拽（如文件上传）
      if (target.closest('[draggable="true"]') && !target.closest('.no-drag')) {
        return;
      }

      e.preventDefault();
    };

    // 禁用F12等开发者工具快捷键（仅生产环境）
    const handleKeyDown = (e: KeyboardEvent) => {
      if (process.env.NODE_ENV === 'production') {
        // 禁用常见的开发者工具快捷键
        if (
          e.key === 'F12' ||
          (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C' || e.key === 'J')) ||
          (e.ctrlKey && e.key === 'U') // 查看源代码
        ) {
          e.preventDefault();
        }
      }
    };

    // 添加事件监听器
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // 返回清理函数
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    // 检查刷新状态
    checkRefreshState();

    // 设置全局事件处理
    const cleanup = setupGlobalEventHandlers();

    // 延迟1秒后开始上传存储的数据，确保页面稳定
    const timer = setTimeout(uploadStoredFeedbacks, 1000);

    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [checkRefreshState, uploadStoredFeedbacks, setupGlobalEventHandlers]);

  // 开启 v7_startTransition（类型可能未更新，这里用 any 断言保证兼容）
  return <RouterProvider router={router} future={{ v7_startTransition: true } as any} />;
}

export default App;
